import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import TicketCard from './components/TicketCard';
import TicketFormModal from './components/TicketFormModal';
import TicketDetailModal from './components/TicketDetailModal';
import {
  supportTickets,
  statusMeta,
  priorityMeta,
  supportStaff,
  type TicketStatus,
  type TicketPriority,
  type Ticket,
  type TicketReply,
} from '@/mocks/adminSupport';

type FilterStatus = TicketStatus | 'todas';

export default function AdminSupport() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('todas');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadTickets = useCallback(() => {
    setLoading(true);
    setError(null);
    setTimeout(() => {
      try {
        setTickets([...supportTickets]);
        setLoading(false);
      } catch {
        setError('No se pudieron cargar los tickets. Verificá tu conexión e intentá de nuevo.');
        setLoading(false);
      }
    }, 600);
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const stats = useMemo(() => {
    return {
      total: tickets.length,
      abierto: tickets.filter((t) => t.status === 'abierto').length,
      proceso: tickets.filter((t) => t.status === 'proceso').length,
      resuelto: tickets.filter((t) => t.status === 'resuelto').length,
      cerrado: tickets.filter((t) => t.status === 'cerrado').length,
    };
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    let result = tickets;
    if (filterStatus !== 'todas') {
      result = result.filter((t) => t.status === filterStatus);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (t) =>
          t.subject.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          t.requester.toLowerCase().includes(q) ||
          t.replies.some((r) => r.message.toLowerCase().includes(q)),
      );
    }
    return result;
  }, [tickets, filterStatus, searchQuery]);

  const handleCreate = useCallback((subject: string, description: string, priority: TicketPriority) => {
    const newTicket: Ticket = {
      id: Math.max(...tickets.map((t) => t.id), 1000) + 1,
      subject,
      description,
      status: 'abierto',
      priority,
      requester: 'Carlos Mendoza',
      requesterEmail: 'carlos@radiofrecuenciaweb.com',
      assignedTo: supportStaff[0],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      replies: [],
    };
    setTickets((prev) => [newTicket, ...prev]);
    setShowCreate(false);
    SwalAlert.toast('Ticket #' + newTicket.id + ' creado exitosamente', 'success');
  }, [tickets]);

  const handleStatusChange = useCallback((ticket: Ticket, newStatus: TicketStatus) => {
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticket.id ? { ...t, status: newStatus, updatedAt: new Date().toISOString() } : t
      )
    );
    setSelectedTicket((prev) =>
      prev?.id === ticket.id ? { ...prev, status: newStatus, updatedAt: new Date().toISOString() } : prev
    );
    SwalAlert.toast('Estado actualizado a "' + statusMeta[newStatus].label + '"', 'success');
  }, []);

  const handlePriorityChange = useCallback((ticket: Ticket, newPriority: TicketPriority) => {
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticket.id ? { ...t, priority: newPriority, updatedAt: new Date().toISOString() } : t
      )
    );
    setSelectedTicket((prev) =>
      prev?.id === ticket.id ? { ...prev, priority: newPriority, updatedAt: new Date().toISOString() } : prev
    );
    SwalAlert.toast('Prioridad cambiada a "' + priorityMeta[newPriority].label + '"', 'success');
  }, []);

  const handleReply = useCallback((ticket: Ticket, message: string) => {
    const newReply: TicketReply = {
      id: Math.max(...ticket.replies.map((r) => r.id), 0) + 1,
      author: 'Carlos Mendoza',
      authorRole: 'soporte',
      message,
      createdAt: new Date().toISOString(),
    };
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticket.id
          ? { ...t, replies: [...t.replies, newReply], status: t.status === 'abierto' ? 'proceso' : t.status, updatedAt: new Date().toISOString() }
          : t
      )
    );
    setSelectedTicket((prev) =>
      prev?.id === ticket.id
        ? { ...prev, replies: [...prev.replies, newReply], status: prev.status === 'abierto' ? 'proceso' : prev.status, updatedAt: new Date().toISOString() }
        : prev
    );
  }, []);

  const handleOpenDetail = useCallback((ticket: Ticket) => {
    setSelectedTicket(ticket);
  }, []);

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-customer-service-2-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Soporte</h1>
              <p className="text-foreground-400 text-[9px]">{stats.abierto} abiertos · {stats.total} total</p>
            </div>
          </div>
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-primary-600 text-base"></i>
              </div>
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Soporte</h2>
                <p className="text-foreground-400 text-xs">
                  {stats.total} tickets · {stats.abierto} abiertos · {stats.proceso} en proceso · {stats.resuelto} resueltos
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/dashboard"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-dashboard-line text-xs"></i>
                Dashboard
              </Link>
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line text-base"></i>
                Crear ticket
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8">
            <div className="max-w-[960px] mx-auto space-y-5">
              {/* Mobile create button */}
              <div className="flex lg:hidden">
                <button
                  onClick={() => setShowCreate(true)}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-bold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line text-base"></i>
                  Crear ticket
                </button>
              </div>

              {/* Stats cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Total', count: stats.total, icon: 'ri-ticket-line', isTotal: true },
                  { label: 'Abiertos', count: stats.abierto, icon: 'ri-mail-open-line', status: 'abierto' as TicketStatus },
                  { label: 'En Proceso', count: stats.proceso, icon: 'ri-loader-4-line', status: 'proceso' as TicketStatus },
                  { label: 'Resueltos', count: stats.resuelto, icon: 'ri-check-double-line', status: 'resuelto' as TicketStatus },
                ].map((s) => {
                  const isActive = s.isTotal
                    ? filterStatus === 'todas'
                    : filterStatus === s.status;
                  const meta = s.isTotal ? null : statusMeta[s.status!];
                  return (
                    <button
                      key={s.label}
                      onClick={() => setFilterStatus(s.isTotal ? 'todas' : s.status!)}
                      className={`rounded-2xl border p-4 text-left transition-all cursor-pointer ${
                        isActive
                          ? 'ring-2 ring-accent-500 ring-offset-1 bg-background-50 border-accent-500/30'
                          : 'bg-background-50 border-background-200 hover:border-background-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${s.isTotal ? 'bg-background-200' : meta!.bgColor}`}>
                          <i className={`${s.icon} text-sm ${s.isTotal ? 'text-foreground-500' : meta!.color}`}></i>
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-foreground-950">{s.count}</p>
                      <p className="text-foreground-400 text-[10px] mt-0.5">{s.label}</p>
                    </button>
                  );
                })}
              </div>

              {/* Search bar */}
              <div className="relative">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por asunto, descripción o solicitante..."
                  className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
                  >
                    <i className="ri-close-line text-foreground-500 text-xs"></i>
                  </button>
                )}
              </div>

              {/* Status filter chips */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                <button
                  onClick={() => setFilterStatus('todas')}
                  className={`shrink-0 px-3.5 py-2 rounded-full text-[11px] font-bold transition-colors cursor-pointer whitespace-nowrap ${
                    filterStatus === 'todas'
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 border border-background-200 text-foreground-500 hover:bg-background-100'
                  }`}
                >
                  Todas ({stats.total})
                </button>
                {(['abierto', 'proceso', 'resuelto', 'cerrado'] as TicketStatus[]).map((st) => {
                  const meta = statusMeta[st];
                  const count = tickets.filter((t) => t.status === st).length;
                  return (
                    <button
                      key={st}
                      onClick={() => setFilterStatus(filterStatus === st ? 'todas' : st)}
                      className={`shrink-0 px-3.5 py-2 rounded-full text-[11px] font-bold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                        filterStatus === st
                          ? 'bg-foreground-950 text-background-50'
                          : 'bg-background-50 border border-background-200 text-foreground-500 hover:bg-background-100'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
                      {meta.label} ({count})
                    </button>
                  );
                })}
              </div>

              {/* LOADING STATE */}
              {loading && (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="bg-background-50 rounded-2xl border border-background-200 p-4 animate-pulse">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2.5">
                          <div className="flex items-center gap-2">
                            <div className="h-4 w-12 rounded-md bg-background-200"></div>
                            <div className="h-5 w-16 rounded-md bg-background-200"></div>
                            <div className="h-5 w-14 rounded-md bg-background-200"></div>
                          </div>
                          <div className="h-4 w-48 rounded-md bg-background-200"></div>
                          <div className="h-3 w-full rounded-md bg-background-200"></div>
                        </div>
                        <div className="shrink-0 space-y-2">
                          <div className="h-3 w-12 rounded-md bg-background-200"></div>
                          <div className="h-5 w-8 rounded-md bg-background-200"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ERROR STATE */}
              {!loading && error && (
                <div className="text-center py-16 bg-background-50 rounded-2xl border border-background-200">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                    <i className="ri-error-warning-line text-red-500 text-xl"></i>
                  </div>
                  <h3 className="text-foreground-800 font-bold text-sm mb-1">Error al cargar tickets</h3>
                  <p className="text-foreground-400 text-xs max-w-xs mx-auto leading-relaxed">{error}</p>
                  <button
                    onClick={loadTickets}
                    className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-accent-500 text-background-50 text-xs font-bold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line text-sm"></i>
                    Reintentar
                  </button>
                </div>
              )}

              {/* CONTENT */}
              {!loading && !error && (
                <>
                  {filteredTickets.length === 0 ? (
                    <div className="text-center py-16 bg-background-50 rounded-2xl border border-background-200">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-4">
                        <i className="ri-ticket-line text-foreground-400 text-xl"></i>
                      </div>
                      <h3 className="text-foreground-800 font-bold text-sm mb-1">Sin tickets</h3>
                      <p className="text-foreground-400 text-xs max-w-xs mx-auto leading-relaxed">
                        {filterStatus === 'todas'
                          ? 'No hay tickets para mostrar.'
                          : 'No hay tickets en estado "' + statusMeta[filterStatus].label + '".'}
                      </p>
                      <button
                        onClick={() => setShowCreate(true)}
                        className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary-500 text-background-50 text-xs font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-add-line text-sm"></i>
                        Crear ticket
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredTickets.map((ticket) => (
                        <TicketCard
                          key={ticket.id}
                          ticket={ticket}
                          onClick={handleOpenDetail}
                          onChangeStatus={handleStatusChange}
                        />
                      ))}
                    </div>
                  )}

                  {/* Info banner */}
                  {filteredTickets.length > 0 && (
                    <div className="bg-background-50 rounded-2xl border border-background-200 p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                        <i className="ri-lightbulb-line text-accent-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-foreground-900 mb-1">Gestión de tickets</h4>
                        <p className="text-xs text-foreground-500 leading-relaxed">
                          Respondé los tickets desde el chat de soporte al hacer clic en cualquiera. El estado cambia automáticamente a <strong>En Proceso</strong> al responder uno <strong>Abierto</strong>. Usá Cmd+Enter para enviar respuestas rápido. Los tickets <strong>Resueltos</strong> y <strong>Cerrados</strong> bloquean nuevas respuestas.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Backend Pending Indicator */}
                  <div className="bg-amber-50/80 rounded-2xl border border-amber-200 p-3 flex items-center gap-2.5">
                    <div className="w-6 h-6 rounded-lg bg-amber-200 flex items-center justify-center shrink-0">
                      <i className="ri-tools-line text-amber-700 text-xs"></i>
                    </div>
                    <p className="text-[10px] text-amber-700 leading-relaxed">
                      <strong>Frontend mock.</strong> Los tickets se guardan solo durante la sesión. La persistencia, adjuntos, notificaciones por email y SLA requerirán integración con backend (Cursor).
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>



      {/* Modals */}
      <TicketFormModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreate}
      />

      <TicketDetailModal
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
        onStatusChange={handleStatusChange}
        onPriorityChange={handlePriorityChange}
        onReply={handleReply}
      />
    </div>
  );
}